import {
  CashDollarIcon,
  ChartLineIcon,
  ClipboardCheckIcon,
  ClockIcon,
  DesktopIcon,
  EmailIcon,
  EyeCheckMarkIcon,
  GiftCardIcon,
  InventoryIcon,
  ListBulletedIcon,
  LocationIcon,
  OrderDraftIcon,
  PackageReturnedIcon,
  PersonExitIcon,
  PersonLockIcon,
  PersonalizedTextIcon,
  PriceListIcon,
  ReceiptIcon,
  SettingsIcon,
  StoreIcon,
  TransferIcon,
  WandIcon,
} from '@shopify/polaris-icons';
import TeamaticalIcon from './TeamaticalPrep/TeamaticalIcon';

// Sidebar and Dashboard sections, in display order. Each tool names its
// group below; navTitle is the shorter label used inside a group
// ("Transfers" under "Shopify"), title is the full name everywhere else.
export const GROUPS = [
  { id: 'shopify', title: 'Shopify' },
  { id: 'lightspeed', title: 'Lightspeed' },
  { id: 'club-stores', title: 'Club Stores' },
  { id: 'marketing', title: 'Marketing' },
  { id: 'catalog', title: 'Catalog' },
  { id: 'amazon', title: 'Amazon' },
  { id: 'people', title: 'People & Access' },
  { id: 'system', title: 'System' },
];

const TOOLS = [
  {
    id: 'transfer-reports',
    title: 'Shopify Transfers',
    navTitle: 'Transfers',
    group: 'shopify',
    icon: TransferIcon,
    description: 'Print out or PDF warehouse transfers including location data',
    path: '/transfer-reports',
    ready: true,
    image: '/images/shopify-transfers.png',
  },
  {
    id: 'purchase-orders',
    title: 'Shopify Purchase Orders',
    navTitle: 'Purchase Orders',
    group: 'shopify',
    icon: ReceiptIcon,
    description: 'Print out or PDF pick sheets for purchase orders with bin locations',
    path: '/purchase-orders',
    ready: true,
    image: '/images/shopify-transfers.png',
  },
  {
    id: 'teamatical-prep',
    title: 'Teamatical Prep',
    navTitle: 'Teamatical Prep',
    group: 'shopify',
    icon: TeamaticalIcon,
    description: 'Link products to Teamatical: metafields, stock at the Teamatical location and the Teamatical shipping profile',
    path: '/teamatical-prep',
    ready: true,
    image: '/images/shopify-transfers.png',
  },
  {
    id: 'lightspeed-vendor-returns',
    title: 'Lightspeed Vendor Returns',
    navTitle: 'Vendor Returns',
    group: 'lightspeed',
    icon: PackageReturnedIcon,
    description: 'Upload CSV files to add items to vendor returns in Lightspeed',
    path: '/lightspeed-vendor-returns',
    ready: true,
    image: '/images/lightspeed-vendor-returns.png',
  },
  {
    id: 'b2b-purchase-orders',
    title: 'B2B → Lightspeed PO',
    navTitle: 'B2B → Lightspeed PO',
    group: 'lightspeed',
    icon: OrderDraftIcon,
    description: 'Copy Shopify B2B orders to Lightspeed as Purchase Orders',
    path: '/b2b-purchase-orders',
    ready: true,
    image: '/images/b2b-lightspeed-po.png',
  },
  {
    id: 'it-assets',
    title: 'IT Assets',
    navTitle: 'IT Assets',
    group: 'people',
    icon: DesktopIcon,
    description: 'Every IT device, who or where it is, what is free to lend, and the receipts behind it',
    path: '/it-assets',
    alsoFor: ['it-assets-view'],
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    // Not shown in the sidebar or on the Dashboard: it exists so the Users page can grant read-only access to IT assets.
    id: 'it-assets-view',
    title: 'IT Assets (view only)',
    navTitle: 'IT Assets (View Only)',
    group: 'people',
    icon: DesktopIcon,
    description: 'See IT assets without changing anything',
    path: '/it-assets',
    hidden: true,
    ready: true,
  },
  {
    id: 'b2b-order-review',
    title: 'B2B Order Review',
    navTitle: 'B2B Order Review',
    group: 'shopify',
    icon: ClipboardCheckIcon,
    description: 'Check SP Corp orders against Lightspeed sales and stock and flag ones that would overstock a store',
    path: '/b2b-order-review',
    ready: true,
    image: '/images/b2b-lightspeed-po.png',
  },
  {
    id: 'accessibility-audit',
    title: 'Accessibility Audit',
    navTitle: 'Accessibility Audit',
    group: 'shopify',
    icon: EyeCheckMarkIcon,
    description: 'WCAG 2.1 AA checks of our Shopify storefronts, with the elements to fix',
    path: '/accessibility-audit',
    ready: true,
    image: '/images/shopify-transfers.png',
  },
  {
    id: 'inventory-rebalancing',
    title: 'Inventory Rebalancing',
    navTitle: 'Inventory Rebalancing',
    group: 'lightspeed',
    icon: InventoryIcon,
    description: 'Suggest and create Lightspeed transfers that balance stock across locations from sales velocity',
    path: '/inventory-rebalancing',
    ready: true,
    image: '/images/lightspeed-vendor-returns.png',
  },
  {
    id: 'inventory-knowledge',
    title: 'Inventory Movement Knowledge',
    navTitle: 'Inventory Knowledge',
    group: 'lightspeed',
    icon: InventoryIcon,
    description: 'Plain-English guidance on how stock should move between stores, hubs and the warehouse, and the feedback people give on each recommendation',
    path: '/inventory-knowledge',
    ready: true,
    image: '/images/lightspeed-vendor-returns.png',
  },
  {
    id: 'gift-cards',
    title: 'Gift Cards',
    navTitle: 'Gift Cards',
    group: 'lightspeed',
    icon: GiftCardIcon,
    description: 'Creates Lightspeed gift cards, singly or in batches with a prefix and value, keeps every job, and prints cut-out sheets with a barcode',
    path: '/gift-cards',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'shopify-gift-cards',
    title: 'Shopify Gift Cards',
    navTitle: 'Gift Cards',
    group: 'shopify',
    icon: GiftCardIcon,
    description: 'Creates gift cards on any of our Shopify stores, one or many at a time, with an expiry and a note on every card; keeps every job, prints cut-out sheets and reads balances back',
    path: '/shopify-gift-cards',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'store-locator',
    title: 'Store Locator',
    navTitle: 'Store Locator',
    group: 'shopify',
    icon: LocationIcon,
    description: 'One list of every store with hours, special hours and notifications, published as the map, the directory and a page per store on every Soccer Post site',
    path: '/store-locator',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'wa-spirit-bopis',
    title: 'Washington Spirit BOPIS',
    navTitle: 'Washington Spirit BOPIS',
    group: 'club-stores',
    icon: StoreIcon,
    description: 'Open and close Buy-Online-Pick-Up-In-Stadium for a Spirit home game',
    path: '/wa-spirit-bopis',
    ready: true,
    image: '/images/spirit-bopis.png',
  },
  {
    id: 'gotham-bopis',
    title: 'Gotham FC BOPIS',
    navTitle: 'Gotham FC BOPIS',
    group: 'club-stores',
    icon: StoreIcon,
    description: 'Open and close Buy-Online-Pick-Up-In-Stadium for a Gotham FC home game',
    path: '/gotham-bopis',
    ready: true,
    image: '/images/gotham-bopis.png',
  },
  {
    id: 'gotham-pos-products',
    title: 'Gotham FC POS Products',
    navTitle: 'Gotham FC POS Products',
    group: 'club-stores',
    icon: StoreIcon,
    description: 'Choose which Gotham FC Shop products are on POS and move stock from Eatontown to SI Stadium',
    path: '/gotham-pos-products',
    ready: true,
    image: '/images/gotham-bopis.png',
  },
  {
    id: 'gotham-post-game-count',
    title: 'Gotham FC Post-Game Count',
    navTitle: 'Gotham FC Post-Game Count',
    group: 'club-stores',
    icon: ClipboardCheckIcon,
    description: 'Count POS stock after a Gotham FC home game against Eatontown minus the game\'s sales, then set Shopify to the count',
    path: '/gotham-post-game-count',
    ready: true,
    image: '/images/gotham-bopis.png',
  },
  {
    id: 'name-kit-reports',
    title: 'Name Kit Reports',
    navTitle: 'Name Kit Reports',
    group: 'club-stores',
    icon: PersonalizedTextIcon,
    description: 'Count player name customizations for Washington Spirit and Gotham FC orders by date range',
    path: '/name-kit-reports',
    ready: true,
    image: '/images/spirit-gotham-namekits.png',
  },
  {
    id: 'club-sales-history',
    title: 'Club Store Sales History',
    navTitle: 'Club Store Sales History',
    group: 'club-stores',
    icon: ChartLineIcon,
    description: 'BOPIS and in-stadium gross sales for every Spirit and Gotham FC home game, live on game day',
    path: '/club-sales-history',
    ready: true,
    image: '/images/spirit-gotham-namekits.png',
  },
  {
    id: 'categorize-and-clean',
    title: 'Categorize & Clean',
    navTitle: 'Categorize & Clean',
    group: 'catalog',
    icon: WandIcon,
    description: 'Category and metafields for every product, reviewed and written from one list',
    path: '/categorize-and-clean',
    ready: true,
    image: '/images/spirit-gotham-namekits.png',
  },
  {
    id: 'sales-velocity',
    title: 'Sales Velocity',
    navTitle: 'Sales Velocity',
    group: 'catalog',
    icon: ChartLineIcon,
    description: 'Units sold per channel for a SKU or style over the last 7, 30, or 90 days',
    path: '/sales-velocity',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'adidas-map',
    title: 'Adidas MAP',
    navTitle: 'Adidas MAP',
    group: 'catalog',
    icon: PriceListIcon,
    description: "Look up the current Adidas Minimum Advertised Price for any SKU, UPC or description and see the season's pricing windows on a calendar",
    path: '/adidas-map',
    ready: true,
    image: '/images/adidas-map.png',
  },
  {
    id: 'offboarding',
    title: 'Offboarding',
    navTitle: 'Offboarding',
    group: 'people',
    icon: PersonExitIcon,
    description: 'Remove a departing employee’s access across every connected system and archive their data',
    path: '/offboarding',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'users',
    title: 'Users',
    navTitle: 'Users',
    group: 'system',
    icon: PersonLockIcon,
    description: 'Manage Utility Hub accounts and who has access to what',
    path: '/users',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'amazon-pricing-hub',
    title: 'Amazon Pricing Hub',
    navTitle: 'Pricing Hub',
    group: 'amazon',
    icon: CashDollarIcon,
    description: 'Amazon listings, buy box, fees, sales and Repricer min/max for the three stores',
    path: '/amazon-pricing-hub',
    ready: true,
    image: '/images/amazon-pricing-hub.png',
  },
  {
    id: 'klaviyo-profile-cleanup',
    title: 'Klaviyo Profile Cleanup',
    navTitle: 'Klaviyo Cleanup',
    group: 'marketing',
    icon: EmailIcon,
    description: 'Keeps every Klaviyo profile\'s Local Store a real store and removes bounced Codisto addresses, nightly',
    path: '/klaviyo-profile-cleanup',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  // Settings needs two keys: this `settings` permission (a checkbox on the
  // Users page) and, per module, that module's own permission
  // (frontend/src/Settings/Settings.jsx keeps the sections).
  {
    id: 'settings',
    title: 'Settings',
    navTitle: 'Settings',
    group: 'system',
    icon: SettingsIcon,
    description: 'Settings for the tools you use, in one place',
    path: '/settings',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'activity-log',
    title: 'Activity',
    navTitle: 'Activity',
    group: 'system',
    icon: ListBulletedIcon,
    description: 'Everything done on Utility Hub: sign-ins and every action in every module, searchable and filterable',
    path: '/activity',
    ready: true,
    image: '/images/cron-monitor.png',
  },
  {
    id: 'cron-monitor',
    title: 'Cron Monitor',
    navTitle: 'Cron Monitor',
    group: 'system',
    icon: ClockIcon,
    description: 'Monitor cron job runs, timing, CPU & memory usage with timeline visualization',
    path: '/cron-monitor',
    ready: true,
    image: '/images/cron-monitor.png',
  },
];

// Every tool is gated the same way now: visible only to accounts whose
// permissions array (from /api/auth/me) includes this tool's id. The
// server-side check (require_permission in app.py) is the real gate —
// this is just so nav/dashboard don't show dead links. LVR and Cron
// Monitor are listed here for nav purposes but their own backend services
// don't enforce this yet (out of scope for the Users module build — any
// valid login still reaches them directly).
// An admin sees every tool, including ones added after their account was made.
export const visibleTools = (permissions) => {
  const has = (id) => (permissions || []).includes(id);
  return TOOLS.filter((t) => !t.hidden && (has('admin') || has(t.id) || (t.alsoFor || []).some(has)));
};

// visibleTools, bucketed into GROUPS order; empty groups are dropped.
export const groupedTools = (permissions) => {
  const tools = visibleTools(permissions);
  return GROUPS
    .map((g) => ({ ...g, tools: tools.filter((t) => t.group === g.id) }))
    .filter((g) => g.tools.length > 0);
};

export default TOOLS;
